import { TextStyle, View, ViewStyle } from "react-native"

import MaterialIcons from "@expo/vector-icons/MaterialIcons"
import { Image, ImageStyle } from "expo-image"

import { Text } from "src/components/Ignite/Text"
import { useAppTheme } from "src/hooks/useAppTheme"
import { ThemedStyle } from "src/theme"

interface ResourceThumbnailProps {
  resourceType: string
  resourceId: string
  title?: string
  thumbnailUrl?: string
  onThumbnailError?: () => void
}

export const ResourceThumbnail = ({
  resourceType,
  resourceId,
  title,
  thumbnailUrl,
  onThumbnailError,
}: ResourceThumbnailProps) => {
  const { themed, theme } = useAppTheme()

  const getIconName = () => {
    switch (resourceType.toLowerCase()) {
      case "pdf":
        return "picture-as-pdf"
      case "pptx":
        return "slideshow"
      case "docx":
        return "description"
      default:
        return "insert-drive-file"
    }
  }

  const getResourceTitle = () => {
    return title ?? `${resourceId}.${resourceType}`
  }

  return (
    <View style={themed($container)}>
      <View style={themed($iconContainer)}>
        {thumbnailUrl ? (
          <Image
            source={{ uri: thumbnailUrl }}
            style={$thumbnailImage}
            recyclingKey={`resource-thumbnail-${resourceId}`}
            onError={onThumbnailError}
          />
        ) : (
          <MaterialIcons name={getIconName()} size={60} color={theme.colors.buttonText} />
        )}
      </View>
      <View style={themed($textContainer)}>
        <Text
          text={getResourceTitle()}
          style={themed($resourceTitle)}
          numberOfLines={4}
          ellipsizeMode="tail"
        />
      </View>
      <View style={themed($openIconContainer)}>
        <MaterialIcons name="open-in-new" size={32} color={theme.colors.buttonText} />
      </View>
    </View>
  )
}

const $container: ThemedStyle<ViewStyle> = ({ colors, spacing }) => ({
  flexDirection: "row",
  alignItems: "center",
  backgroundColor: colors.buttonBackground,
  borderRadius: 12,
  padding: spacing.sm,
  marginBottom: spacing.sm,
  width: 280,
})

const $iconContainer: ThemedStyle<ViewStyle> = ({ colors, spacing }) => ({
  width: 80,
  height: 80,
  backgroundColor: colors.buttonBackground,
  borderRadius: 8,
  justifyContent: "center",
  alignItems: "center",
  marginRight: spacing.md,
  overflow: "hidden",
})

const $thumbnailImage: ImageStyle = {
  width: "100%",
  height: "100%",
}

const $textContainer: ThemedStyle<ViewStyle> = () => ({
  flex: 1,
  justifyContent: "center",
})

const $resourceTitle: ThemedStyle<TextStyle> = ({ colors, typography }) => ({
  color: colors.buttonText,
  fontSize: 16,
  fontFamily: typography.fonts.figtree.medium,
  marginBottom: 4,
})

const $openIconContainer: ThemedStyle<ViewStyle> = ({ spacing }) => ({
  marginLeft: spacing.sm,
})
